import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import AuthLayout from "../Components/Common/AuthLayout";
import ajaxUser from "../util/remote/ajaxUser";

/**
 * Resetting a forgotten password: ask for a code with your e-mail address, then
 * use the code to set a new one.
 *
 * Both steps live on this page. The code arrives in a mail app, and sending
 * people off to hunt for a second link is where they give up.
 *
 * The answer to step one is the same whether or not the address is on the
 * system, so this cannot be used to find out who has an account.
 */
export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState("request");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error("Enter your e-mail address.");

    setBusy(true);
    const res = await ajaxUser.forgotPassword(email.trim());
    setBusy(false);

    if (res.status !== "OK") {
      return toast.error(res.message || "That did not work. Try again.");
    }
    toast.success(res.message);
    setStep("reset");
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    if (!code.trim()) return toast.error("Enter the code from your e-mail.");
    if (password.length < 6) return toast.error("Choose a password of at least 6 characters.");
    if (password !== confirm) return toast.error("The two passwords do not match.");

    setBusy(true);
    const res = await ajaxUser.resetPassword(email.trim(), code.trim(), password);
    setBusy(false);

    if (res.status !== "OK") {
      return toast.error(res.message || "That did not work. Try again.");
    }
    toast.success(res.message);
    setTimeout(() => navigate("/login", { replace: true }), 1200);
  };

  if (step === "request") {
    return (
      <AuthLayout
        title="Forgotten your password?"
        subtitle="Give us the address you sign in with and we will send you a code."
        footer={<Link to="/login">Back to sign in</Link>}
      >
        <form onSubmit={requestCode}>
          <div className="auth-field">
            <label htmlFor="fp-email">E-mail address</label>
            <div className="auth-input-wrap">
              <input
                id="fp-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@bericot.africa"
                autoComplete="email"
                autoFocus
              />
            </div>
          </div>

          <button type="submit" className="auth-submit" disabled={busy}>
            {busy ? "Sending…" : "Send me a code"}
          </button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Set a new password"
      subtitle={`We have sent a code to ${email}. It works for the next 30 minutes.`}
      footer={
        <>
          Nothing arrived?{" "}
          <button type="button" onClick={() => setStep("request")}>Send another</button>
          {" · "}
          <Link to="/login">Back to sign in</Link>
        </>
      }
    >
      <form onSubmit={resetPassword}>
        <div className="auth-field">
          <label htmlFor="fp-code">Code from the e-mail</label>
          <div className="auth-input-wrap">
            <input
              id="fp-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="000000"
              inputMode="numeric"
              maxLength={6}
              style={{ letterSpacing: "0.35em", fontSize: "1.05rem" }}
              autoFocus
            />
          </div>
        </div>

        <div className="auth-field">
          <label htmlFor="fp-pass">New password</label>
          <div className="auth-input-wrap">
            <input
              id="fp-pass"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              autoComplete="new-password"
            />
          </div>
        </div>

        <div className="auth-field">
          <label htmlFor="fp-confirm">Type it again</label>
          <div className="auth-input-wrap">
            <input
              id="fp-confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
            />
          </div>
        </div>

        <button type="submit" className="auth-submit" disabled={busy}>
          {busy ? "Saving…" : "Change my password"}
        </button>
      </form>
    </AuthLayout>
  );
}
